import React, { useState, useEffect, useCallback } from 'react';
import {
  Puzzle,
  MessageSquare,
  CreditCard,
  CheckCircle,
  AlertCircle,
  Clock,
  RefreshCw,
  ExternalLink,
  Unlink,
} from 'lucide-react';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import DefaultSMSConfig from '../../features/settings/components/DefaultSMSConfig';
import { useRole } from '../../context/RoleContext';
import { useAuth } from '../../context/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// ── Stripe Connect Card ────────────────────────────────────────────────────────

const StripeConnectCard = () => {
  const { user: currentUser, profile } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();

  const [status, setStatus]           = useState(null);   // null=loading
  const [connecting, setConnecting]   = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);
  const [fetchError, setFetchError]   = useState(null);

  const companyId = profile?.companyId;

  const fetchStatus = useCallback(async () => {
    if (!companyId || !currentUser) return;
    setFetchError(null);
    try {
      const token = await currentUser.getIdToken();
      const res   = await fetch(
        `${API_URL}/api/stripe/connect/status?companyId=${companyId}`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      if (!res.ok) throw new Error('Failed to fetch status');
      setStatus(await res.json());
    } catch {
      setFetchError('Could not load Stripe connection status.');
    }
  }, [companyId, currentUser]);

  // Handle redirect-back query params from Stripe onboarding
  useEffect(() => {
    const param = searchParams.get('stripe');
    if (param === 'connected') {
      toast.success('Stripe onboarding complete — checking status…');
      setSearchParams({}, { replace: true });
    } else if (param === 'refresh') {
      toast.info('Stripe onboarding was not completed. Click "Resume" to continue.');
      setSearchParams({}, { replace: true });
    }
    fetchStatus();
  }, [companyId]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleConnect = async () => {
    if (!companyId || !currentUser) return;
    setConnecting(true);
    try {
      const token = await currentUser.getIdToken();
      const res   = await fetch(`${API_URL}/api/stripe/connect/create-link`, {
        method:  'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body:    JSON.stringify({ companyId }),
      });
      // Read the body exactly once
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Failed to create link');
      if (!data.url) throw new Error('No onboarding URL returned from server');
      // Redirect in the same tab so Stripe can return to return_url correctly
      window.location.href = data.url;
      // connecting spinner stays visible until the page navigates away
    } catch (e) {
      toast.error(e.message || 'Failed to start Stripe onboarding');
      setConnecting(false);
    }
  };

  const handleDisconnect = async () => {
    if (
      !companyId ||
      !currentUser ||
      !window.confirm(
        'Disconnect Stripe? Payments will fall back to the platform account until you reconnect.',
      )
    ) return;

    setDisconnecting(true);
    try {
      const token = await currentUser.getIdToken();
      const res   = await fetch(`${API_URL}/api/stripe/connect/disconnect`, {
        method:  'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body:    JSON.stringify({ companyId }),
      });
      if (!res.ok) throw new Error('Failed to disconnect');
      toast.success('Stripe account disconnected');
      setStatus({ status: 'not_connected', connectAccountId: null, accountInfo: {} });
    } catch (e) {
      toast.error(e.message || 'Failed to disconnect');
    } finally {
      setDisconnecting(false);
    }
  };

  // ── Loading skeleton ──
  if (!status && !fetchError) {
    return (
      <div className="rounded-xl border border-gray-200 bg-white p-5">
        <div className="flex items-center gap-3 animate-pulse">
          <div className="w-10 h-10 bg-gray-100 rounded-lg" />
          <div className="flex-1 space-y-2">
            <div className="h-4 bg-gray-100 rounded w-1/3" />
            <div className="h-3 bg-gray-100 rounded w-1/2" />
          </div>
        </div>
      </div>
    );
  }

  // ── Error state ──
  if (fetchError) {
    return (
      <div className="rounded-xl border border-red-100 bg-red-50 p-4 flex items-center justify-between">
        <span className="flex items-center gap-2 text-sm text-red-700">
          <AlertCircle className="w-4 h-4" />
          {fetchError}
        </span>
        <button
          onClick={fetchStatus}
          className="text-xs text-red-600 hover:text-red-800 flex items-center gap-1 transition-colors"
        >
          <RefreshCw className="w-3 h-3" /> Retry
        </button>
      </div>
    );
  }

  const connectStatus  = status?.status || 'not_connected';
  const accountId      = status?.connectAccountId;
  const shortId        = accountId
    ? `${accountId.slice(0, 8)}…${accountId.slice(-4)}`
    : null;
  const chargesEnabled = status?.accountInfo?.chargesEnabled;

  return (
    <div
      className="rounded-xl border border-gray-200 bg-white p-5 space-y-4"
      data-testid="stripe-connect-card"
    >
      {/* Header row */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-[#635BFF] rounded-lg flex items-center justify-center flex-shrink-0">
            <CreditCard className="w-5 h-5 text-white" />
          </div>
          <div>
            <p className="text-sm font-semibold text-gray-900">Stripe Payments</p>
            <p className="text-xs text-gray-500">
              Receive payments directly in your Stripe account
            </p>
          </div>
        </div>

        {/* Status pill */}
        {connectStatus === 'active' && (
          <span
            data-testid="stripe-status-active"
            className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-emerald-50 text-emerald-700 border border-emerald-100"
          >
            <CheckCircle className="w-3.5 h-3.5" /> Connected
          </span>
        )}
        {connectStatus === 'pending' && (
          <span
            data-testid="stripe-status-pending"
            className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-amber-50 text-amber-700 border border-amber-100"
          >
            <Clock className="w-3.5 h-3.5" /> Pending
          </span>
        )}
        {(connectStatus === 'not_connected' || connectStatus === 'restricted') && (
          <span
            data-testid="stripe-status-not-connected"
            className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-500"
          >
            Not Connected
          </span>
        )}
      </div>

      {/* Info banners */}
      {connectStatus === 'active' && (
        <div className="bg-emerald-50 border border-emerald-100 rounded-lg p-3 space-y-0.5">
          <p className="text-xs font-medium text-emerald-800">
            All payments routing to your connected Stripe account
          </p>
          {shortId && (
            <p className="text-xs font-mono text-emerald-700">{shortId}</p>
          )}
          {chargesEnabled && (
            <p className="text-xs text-emerald-600">Charges enabled — ready to receive payments</p>
          )}
        </div>
      )}

      {connectStatus === 'pending' && (
        <div className="bg-amber-50 border border-amber-100 rounded-lg p-3">
          <p className="text-xs text-amber-800">
            Your Stripe account is pending verification. Complete the onboarding
            flow to activate payments.
          </p>
        </div>
      )}

      {(connectStatus === 'not_connected' || connectStatus === 'restricted') && (
        <div className="bg-gray-50 border border-gray-100 rounded-lg p-3">
          <p className="text-xs text-gray-600">
            Connect a Stripe Express account so payments land directly in your bank.
            Your clients pay you — not a middleman.
          </p>
        </div>
      )}

      {/* Action row */}
      <div className="flex items-center gap-2 pt-1">
        {(connectStatus === 'not_connected' || connectStatus === 'restricted') && (
          <button
            onClick={handleConnect}
            disabled={connecting}
            data-testid="stripe-connect-btn"
            className="flex items-center gap-2 px-4 py-2 bg-[#635BFF] hover:bg-[#4F46E5] disabled:opacity-50 text-white text-sm font-medium rounded-lg transition-colors"
          >
            {connecting ? (
              <><RefreshCw className="w-4 h-4 animate-spin" /> Connecting…</>
            ) : (
              <><ExternalLink className="w-4 h-4" /> Connect Stripe</>
            )}
          </button>
        )}

        {connectStatus === 'pending' && (
          <>
            <button
              onClick={handleConnect}
              disabled={connecting}
              data-testid="stripe-resume-btn"
              className="flex items-center gap-2 px-4 py-2 bg-amber-500 hover:bg-amber-600 disabled:opacity-50 text-white text-sm font-medium rounded-lg transition-colors"
            >
              {connecting ? (
                <><RefreshCw className="w-4 h-4 animate-spin" /> Loading…</>
              ) : (
                <><ExternalLink className="w-4 h-4" /> Resume Onboarding</>
              )}
            </button>
            <button
              onClick={fetchStatus}
              title="Refresh status"
              className="p-2 text-gray-400 hover:text-gray-600 transition-colors rounded-lg hover:bg-gray-50"
            >
              <RefreshCw className="w-4 h-4" />
            </button>
          </>
        )}

        {connectStatus === 'active' && (
          <div className="flex items-center justify-between w-full">
            <button
              onClick={fetchStatus}
              className="flex items-center gap-1.5 text-xs text-gray-500 hover:text-gray-700 transition-colors"
            >
              <RefreshCw className="w-3.5 h-3.5" /> Refresh
            </button>
            <button
              onClick={handleDisconnect}
              disabled={disconnecting}
              data-testid="stripe-disconnect-btn"
              className="flex items-center gap-1.5 text-xs text-red-500 hover:text-red-700 disabled:opacity-50 transition-colors"
            >
              <Unlink className="w-3.5 h-3.5" />
              {disconnecting ? 'Disconnecting…' : 'Disconnect'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

// ── Page ──────────────────────────────────────────────────────────────────────

const Integrations = () => {
  const { role } = useRole();

  return (
    <div className="space-y-8">
      {/* Page header */}
      <div className="flex items-center gap-3">
        <Puzzle className="h-6 w-6 text-primary" />
        <div>
          <h2 className="text-2xl font-bold text-secondary">Integrations</h2>
          <p className="text-sm text-muted-foreground">
            Manage connected services and messaging infrastructure.
          </p>
        </div>
      </div>

      {/* SMS & Messaging */}
      <section data-testid="sms-messaging-section">
        <div className="flex items-center gap-2 mb-4">
          <MessageSquare className="w-5 h-5 text-gray-500" />
          <h3 className="text-base font-semibold text-gray-900">SMS & Messaging</h3>
        </div>
        <DefaultSMSConfig userRole={role} />
      </section>

      {/* Stripe Payments */}
      <section data-testid="stripe-payments-section">
        <div className="flex items-center gap-2 mb-4">
          <CreditCard className="w-5 h-5 text-gray-500" />
          <h3 className="text-base font-semibold text-gray-900">Payments</h3>
        </div>
        <StripeConnectCard />
      </section>
    </div>
  );
};

export default Integrations;
